import { useTranslation } from "react-i18next";
import React, { useState, useEffect, useMemo } from "react";
import { SearchOutlined, Modal, Table, Input, Switch, Avatar, Select, Tooltip, message } from "src/assets/imports/antdComponents";
import { ActionTypes, ICollaboratorData, ICollaboratorsTableProps, IResponse, ModuleTypes, roleAccessType, userAccessType } from "../../models/interfaces";
import { getInitials } from "../../transformer";
import { addUsers, addUsersAdmin, getAllUser, getAllUserAdmin } from "../../serviceHelper";
import { updateSelectedStudy, updateStudyCollaboratorData } from "../../states/studiesSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/store";
import { updateStudyOwner } from "src/pages/AdminCenter/serviceHelper";
import { setStudyCollaborators } from "src/pages/AdminCenter/states/studyManagementSlice";
import { ModuleType } from "src/components/SubHeader/interface";
import { addCollaborators } from "src/pages/Reports/serviceHelper";
import { updateReportCollaboratorData, updateSelectedReport } from "src/pages/Reports/states/reportsSlice";
import { ReportPublishedStatusType } from "src/pages/Reports/models/interface";
import { getDTAllUsers, addDTCollaborator, transferDTProjectOwner } from "src/pages/DTProjects/serviceHelper";
import { updateDTProjectCollaborators } from "src/pages/DTProjects/states/dtProjectsSlice";
import { addPersonaProjectCollaborator, getPersonaProjectAllUsers, transferPersonaProjectOwner } from "src/pages/PersonasHome/serviceHelper";
import { updatePersonaProjectCollaborators } from "src/pages/PersonasHome/states/personasHomeSlice";
import "./style.css";
import { useActionTracking } from "src/components/UserMonitoring/useActionTracking";

const modalStyles = {
	header: { height: "4.5rem", padding: "1.25rem 1.5rem", margin: 0 },
	body: { padding: "1.5rem", height: "37rem" },
	mask: { backdropFilter: "blur(5px)" },
	footer: { padding: "0.5rem 1.5rem", margin: 0 },
	content: { padding: 0, margin: 0 },
};

const KNOWN_DT_COLLABORATOR_ERRORS = new Set([
	"Only the project owner can manage collaborators",
	"Only the project owner can perform this action",
	"new owner must have Full Access on the project",
	"You do not have access to this project",
	"Editor access is required for this project",
]);

const getKnownDTErrorMessage = (error: unknown): string | null => {
	const payload = (error as { response?: { data?: { detail?: unknown; message?: unknown; error?: unknown } } })?.response?.data;
	const candidates = [payload?.detail, payload?.message, payload?.error];
	for (const candidate of candidates) {
		if (typeof candidate === "string" && KNOWN_DT_COLLABORATOR_ERRORS.has(candidate)) {
			return candidate;
		}
	}
	return null;
};

const AddCollaboratorModal: React.FC<ICollaboratorsTableProps> = ({
	folderId,
	fullAccessUsers,
	handleCancel,
	isModalOpen,
	onRefreshStudies,
	projectId,
	projectName,
	renderedFrom,
	reportIterationId,
	reportStatus,
	studyId,
	studyName,
}) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const [collaborators, setCollaborators] = useState<ICollaboratorData[]>([]);
	const [searchText, setSearchText] = useState("");
	const [loading, setLoading] = useState(true);
	const [loadingSwitchEdit, setLoadingSwitchEdit] = useState<{ [key: string]: boolean }>({});
	const [loadingSwitchView, setLoadingSwitchView] = useState<{ [key: string]: boolean }>({});
	const [fullAccessUserState, setFullAccessUserState] = useState<ICollaboratorData[]>(fullAccessUsers);
	const [initialLoad, setInitialLoad] = useState(true);
	const [isEditingOwner, setIsEditingOwner] = useState(false);
	const { metaDetails, role } = useSelector((state: RootState) => state.studiesSlice);
	const { selectedReport } = useSelector((state: RootState) => state.reportsSlice);

	const isAdmin = role === roleAccessType.Admin;
	const logInUser = metaDetails?.id;

	const currentOwner = useMemo(() => fullAccessUserState.find((user) => user.is_owner === true), [fullAccessUserState]);
	const logInUserDetails = useMemo(() => collaborators.find((user) => user.collaborator_id === logInUser), [collaborators, logInUser]);

	const renderedFromViewReports = renderedFrom === ModuleType.ViewReports;
	const renderedFromReports = renderedFrom === ModuleType.Reports;
	const renderedFromDTProjects = renderedFrom === ModuleType.DTProjects;
	const renderedFromPersonaProjects = renderedFrom === ModuleType.PersonaProjects;
	const onlyViewAllowed = reportStatus === ReportPublishedStatusType.Published && renderedFromReports ? true : false;

	const { trackAction } = useActionTracking(ModuleTypes.Studies);

	useEffect(() => {
		if (isModalOpen && initialLoad) {
			setFullAccessUserState(fullAccessUsers);
			fetchCollaborators();
			setInitialLoad(false);
		}
	}, [isModalOpen, initialLoad, fullAccessUsers]);

	const fetchCollaborators = async () => {
		try {
			let response: IResponse;
			if (renderedFromPersonaProjects) {
				response = await getPersonaProjectAllUsers(projectId || "");
			} else if (renderedFromDTProjects) {
				response = await getDTAllUsers(projectId || "");
			} else if (isAdmin) {
				response = await getAllUserAdmin(studyId);
			} else {
				response = await getAllUser(studyId);
			}
			if (response.data) {
				const updatedCollaborators = response?.data.map((collaborator) => {
					const existingUser = fullAccessUsers.find((user) => user.collaborator_id === collaborator.collaborator_id);

					return {
						...collaborator,
						is_owner: existingUser?.is_owner || false,
						user_access_type: existingUser?.user_access_type || userAccessType.none,
					};
				});

				const filteredCollaborators = updatedCollaborators.filter((user) => !user.is_owner);

				const sortedCollaborators = filteredCollaborators.sort((a, b) => {
					const accessOrder = {
						[userAccessType.edit]: 0,
						[userAccessType.view]: 1,
						[userAccessType.none]: 2,
					};
					return accessOrder[a.user_access_type] - accessOrder[b.user_access_type];
				});

				setCollaborators(sortedCollaborators);
			}
		} catch {
			if (renderedFromDTProjects) {
				message.error(t("Unable to load collaborators"));
			}
		} finally {
			setLoading(false);
		}
	};

	const handleSearch = (value: string) => {
		setSearchText(value);
	};

	const handleOwnerChange = async (newOwnerId: string) => {
		try {
			const newOwner = collaborators.find((user) => user.collaborator_id === newOwnerId);
			if (!newOwner) return;

			if (renderedFromDTProjects || renderedFromPersonaProjects) {
				const ownerPayload = {
					collaborator_id: newOwner.collaborator_id,
					collaborator_name: newOwner.collaborator_name,
					collaborator_email: newOwner.collaborator_email || "",
				};
				const response = renderedFromPersonaProjects
					? await transferPersonaProjectOwner({
						...ownerPayload,
						project_id: projectId || "",
					})
					: await transferDTProjectOwner(projectId || "", ownerPayload);

				if (response) {
					const previousOwnerAccess = renderedFromDTProjects ? userAccessType.edit : userAccessType.none;
					const updatedFullAccessUsers = fullAccessUserState.map((user) => ({
						...user,
						is_owner: user.collaborator_id === newOwnerId ? true : false,
						user_access_type: user.collaborator_id === currentOwner?.collaborator_id ? previousOwnerAccess : user.user_access_type,
					}));

					const allCollaborators = [...collaborators];
					if (currentOwner && !allCollaborators.some((collab) => collab.collaborator_id === currentOwner.collaborator_id)) {
						allCollaborators.push({ ...currentOwner, is_owner: false, user_access_type: previousOwnerAccess });
					}
					const updatedCollaborators = allCollaborators
						.map((collab) => {
							if (collab.collaborator_id === currentOwner?.collaborator_id) return { ...collab, is_owner: false, user_access_type: previousOwnerAccess };
							if (collab.collaborator_id === newOwnerId) return { ...collab, is_owner: true, user_access_type: userAccessType.edit };
							return collab;
						})
						.filter((user) => !user.is_owner);

					setFullAccessUserState(updatedFullAccessUsers);
					setCollaborators(updatedCollaborators);
					if (renderedFromPersonaProjects) {
						dispatch(updatePersonaProjectCollaborators({ project_id: projectId || "", collaborators: updatedFullAccessUsers }));
					} else {
						dispatch(updateDTProjectCollaborators({ project_id: projectId || "", collaborators: updatedFullAccessUsers }));
					}
				}
			} else {
				const response = await updateStudyOwner({
					collaborator_id: newOwner.collaborator_id,
					collaborator_name: newOwner.collaborator_name,
					collaborator_email: newOwner.collaborator_email,
					user_access_type: userAccessType.edit,
					study_id: studyId,
					is_owner: true,
				});

				if (response) {
					const updatedFullAccessUsers = fullAccessUserState.map((user) => ({
						...user,
						is_owner: user.collaborator_id === newOwnerId ? true : false,
						user_access_type: user.collaborator_id === currentOwner?.collaborator_id ? userAccessType.none : user.user_access_type,
					}));

					const allCollaborators = [...collaborators];
					if (currentOwner && !allCollaborators.some((collab) => collab.collaborator_id === currentOwner.collaborator_id)) {
						allCollaborators.push({
							...currentOwner,
							is_owner: false,
							user_access_type: userAccessType.none,
						});
					}
					const updatedCollaborators = allCollaborators
						.map((collab) => {
							if (collab.collaborator_id === currentOwner?.collaborator_id) {
								return { ...collab, is_owner: false, user_access_type: userAccessType.none };
							}
							if (collab.collaborator_id === newOwnerId) {
								return { ...collab, is_owner: true, user_access_type: userAccessType.edit };
							}
							return collab;
						})
						.filter((user) => !user.is_owner);

					setFullAccessUserState(updatedFullAccessUsers);
					setCollaborators(updatedCollaborators);
					dispatch(
						updateStudyCollaboratorData({
							collab_details: updatedFullAccessUsers,
							study_id: studyId,
						}),
					);

					const updatedOwner = {
						user_id: newOwner.collaborator_id,
						full_name: newOwner.collaborator_name,
						email: newOwner.collaborator_email,
					};

					dispatch(
						updateSelectedStudy({
							study_owner: updatedOwner,
							study_collaborators: updatedFullAccessUsers,
						}),
					);
					if (isAdmin) {
						const filteredFullAccessUsers = updatedFullAccessUsers
							.filter((user) => user.user_access_type !== userAccessType.none)
							.sort((a, b) => {
								if (a.collaborator_id === newOwnerId) return -1;
								if (b.collaborator_id === newOwnerId) return 1;
								return 0;
							});
						dispatch(setStudyCollaborators({ collaborators: filteredFullAccessUsers, study_id: studyId }));
					}
				}
			}
		} catch (error) {
			if (renderedFromDTProjects) {
				message.error(t(getKnownDTErrorMessage(error) || "Unable to update project owner"));
			}
		} finally {
			setIsEditingOwner(false);
		}
	};

	const handleToggle = async (record: ICollaboratorData, accessType: string) => {
		if (!isAdmin && logInUserDetails?.user_access_type === userAccessType.view && accessType === userAccessType.edit) {
			return;
		}

		const setLoading = accessType === userAccessType.edit ? setLoadingSwitchEdit : setLoadingSwitchView;
		setLoading((prevState) => ({
			...prevState,
			[record.collaborator_id]: true,
		}));

		try {
			let updatedAccessType;
			if (!isAdmin && logInUserDetails?.user_access_type === userAccessType.view) {
				updatedAccessType = accessType === userAccessType.view ? userAccessType.view : userAccessType.none;
			} else {
				if (accessType === userAccessType.view) {
					updatedAccessType = userAccessType.view;
				} else if (accessType === userAccessType.edit) {
					updatedAccessType = userAccessType.edit;
				} else {
					updatedAccessType = userAccessType.none;
				}
			}

			let response: IResponse;
			if (renderedFromPersonaProjects) {
				response = await addPersonaProjectCollaborator({
					collaborator_id: record.collaborator_id,
					collaborator_name: record.collaborator_name,
					collaborator_email: record.collaborator_email || "",
					user_access_type: updatedAccessType,
					project_id: projectId || "",
					is_owner: false,
				});
			} else if (renderedFromDTProjects) {
				response = await addDTCollaborator(projectId || "", {
					collaborator_id: record.collaborator_id,
					collaborator_name: record.collaborator_name,
					collaborator_email: record.collaborator_email || "",
					user_access_type: updatedAccessType,
					is_owner: false,
				});
			} else if (renderedFromViewReports || renderedFromReports) {
				response = await addCollaborators({
					report_iteration_id: reportIterationId ?? "",
					collaborator_id: record.collaborator_id,
					collaborator_name: record.collaborator_name,
					collaborator_email: record.collaborator_email,
					user_access_type: updatedAccessType,
					is_owner: false,
				});
			} else if (isAdmin) {
				response = await addUsersAdmin({
					collaborator_id: record.collaborator_id,
					collaborator_name: record.collaborator_name,
					collaborator_email: record.collaborator_email,
					user_access_type: updatedAccessType,
					study_id: studyId,
					is_owner: false,
				});
			} else {
				response = await addUsers({
					collaborator_id: record.collaborator_id,
					collaborator_name: record.collaborator_name,
					collaborator_email: record.collaborator_email,
					user_access_type: updatedAccessType,
					study_id: studyId,
					is_owner: false,
				});
			}

			if (response) {
				const updatedCollaborators = collaborators.map((collaborator) =>
					collaborator.collaborator_id === record.collaborator_id ? { ...collaborator, user_access_type: updatedAccessType } : collaborator,
				);

				trackAction(ActionTypes.ACCESS_UPDATED, { ...record, feature: true, type: undefined });

				setCollaborators(updatedCollaborators);

				const updatedFullAccessUsers = [...fullAccessUserState];
				const userIndex = updatedFullAccessUsers.findIndex((user) => user.collaborator_id === record.collaborator_id);

				if (updatedAccessType === userAccessType.edit || updatedAccessType === userAccessType.view) {
					const newUserData = {
						...record,
						user_access_type: updatedAccessType,
						is_owner: false,
					};

					if (userIndex !== -1) {
						updatedFullAccessUsers[userIndex] = newUserData;
					} else {
						updatedFullAccessUsers.push(newUserData);
					}
				} else {
					if (userIndex !== -1) {
						updatedFullAccessUsers.splice(userIndex, 1);
					}
				}

				setFullAccessUserState(updatedFullAccessUsers);
				if (renderedFromPersonaProjects) {
					dispatch(
						updatePersonaProjectCollaborators({
							project_id: projectId || "",
							collaborators: updatedFullAccessUsers,
						}),
					);
				} else if (renderedFromDTProjects) {
					dispatch(
						updateDTProjectCollaborators({
							project_id: projectId || "",
							collaborators: updatedFullAccessUsers,
						}),
					);
				} else if (renderedFromViewReports || renderedFromReports) {
					dispatch(
						updateReportCollaboratorData({
							collab_details: updatedFullAccessUsers,
							report_iteration_id: reportIterationId,
						}),
					);
					dispatch(
						updateSelectedReport({
							permissions: {
								...selectedReport.permissions, // Preserve other permission properties
								collaborators: updatedFullAccessUsers,
							},
						}),
					);
				} else {
					dispatch(
						updateStudyCollaboratorData({
							collab_details: updatedFullAccessUsers,
							study_id: studyId,
						}),
					);
					dispatch(
						updateSelectedStudy({
							study_collaborators: updatedFullAccessUsers,
						}),
					);
					if (folderId && onRefreshStudies) {
						onRefreshStudies(folderId);
					}
				}
				if (isAdmin && !renderedFromDTProjects && !renderedFromPersonaProjects) {
					dispatch(setStudyCollaborators({ collaborators: updatedFullAccessUsers, study_id: studyId }));
				}
			}
		} catch (error) {
			if (renderedFromDTProjects) {
				message.error(t(getKnownDTErrorMessage(error) || "Unable to update collaborator access"));
			}
		} finally {
			setLoading((prevState) => ({
				...prevState,
				[record.collaborator_id]: false,
			}));
		}
	};

	const columns = [
		{
			title: t("Collaborator Name"),
			dataIndex: "collaborator_name",
			key: "collaborator_name",
			filteredValue: [searchText],
			ellipsis: true,
			onFilter: (value: string, record: ICollaboratorData) =>
				record?.collaborator_name?.toLowerCase().includes(value.toLowerCase()) || record?.collaborator_email?.toLowerCase().includes(value.toLowerCase()),
			render: (_: unknown, record: ICollaboratorData) => (
				<div className="flex items-center">
					<Avatar size="small" className={`navbar-icon flex items-center justify-center mr-4 ${record.is_active === false ? "inactive-avatar" : ""}`}>
						{getInitials(record.collaborator_name)}
					</Avatar>
					<div className="flex flex-col">
						<span className="text-base-1 font-medium secondary-color">{record.collaborator_name}</span>
						<Tooltip title={<span className="text-[x-small] tertiary-color">{record.collaborator_email}</span>}>
							<span className="text-[x-small] tertiary-color">{record.collaborator_email}</span>
						</Tooltip>
					</div>
				</div>
			),
		},
		{
			title: t("View"),
			key: "view",
			render: (_: unknown, record: ICollaboratorData) => (
				<Tooltip title={record?.collaborator_id !== logInUser && record.user_access_type === userAccessType.edit && t("Disable Edit access to proceed!")}>
					<Switch
						size="small"
						checked={record.user_access_type === userAccessType.view || record.user_access_type === userAccessType.edit}
						onChange={(checked) => handleToggle(record, checked ? userAccessType.view : userAccessType.none)}
						disabled={
							record.user_access_type === userAccessType.edit ||
							(!isAdmin &&
								((logInUserDetails?.user_access_type === userAccessType.view && record.user_access_type === (userAccessType.edit as userAccessType)) ||
									record.is_owner ||
									record.collaborator_id === logInUser))
						}
						loading={loadingSwitchView[record.collaborator_id]}
					/>
				</Tooltip>
			),
			width: "12.5%",
		},
		{
			title: t("Edit"),
			key: "edit",
			render: (_: unknown, record: ICollaboratorData) => (
				<Tooltip title={onlyViewAllowed ? t("Disabled for published reports") : logInUserDetails?.user_access_type === userAccessType.view ? t("No access") : ""}>
					<Switch
						size="small"
						checked={record.user_access_type === userAccessType.edit}
						onChange={(checked) => handleToggle(record, checked ? userAccessType.edit : userAccessType.view)}
						disabled={
							onlyViewAllowed ||
							(!isAdmin &&
								(logInUserDetails?.user_access_type === userAccessType.view ||
									record.is_owner ||
									record.collaborator_id === logInUser ||
									(logInUserDetails?.user_access_type === (userAccessType.view as userAccessType) && record.user_access_type === userAccessType.edit)))
						}
						loading={loadingSwitchEdit[record.collaborator_id]}
					/>
				</Tooltip>
			),
			width: "12.5%",
		},
	];

	const editAccessUsers = useMemo(() => {
		return collaborators.filter((user) => user.user_access_type === userAccessType.edit && !user.is_owner && user.collaborator_id !== currentOwner?.collaborator_id);
	}, [collaborators, currentOwner?.collaborator_id]);

	const filteredDataSource = useMemo(() => {
		return collaborators
			.filter((user) => !user.is_owner)
			.filter((collaborator) => [collaborator.collaborator_name, collaborator.collaborator_email].some((field) => field.toLowerCase().includes(searchText.toLowerCase())));
	}, [collaborators, searchText]);

	return (
		<Modal
			title={
				<div className="flex items-center">
					<span>{t("Add Collaborators")}</span>
					{(studyName || projectName) && (
						<>
							<span className="border-l-2 border-[--secondary-color-opacity-25] h-6 mx-4"></span>
							<span className="text-[--secondary-color-lighter] text-lg w-72 truncate" title={projectName || studyName}>
								{projectName || studyName}
							</span>
						</>
					)}
				</div>
			}
			open={isModalOpen}
			onCancel={handleCancel}
			width={500}
			footer={null}
			wrapClassName=""
			centered
			styles={modalStyles}>
			<div className="flex flex-col">
				{currentOwner && (
					<div className="p-2 border border-[--is-owner-color] rounded-[12px] mb-4">
						{isEditingOwner ? (
							<Select
								className="w-full custom-select-no-border"
								placeholder={t("Select new owner")}
								onChange={handleOwnerChange}
								onBlur={() => setIsEditingOwner(false)}
								autoFocus
								optionLabelProp="label"
								notFoundContent={t("No user with edit access")}>
								{editAccessUsers.map((user) => (
									<Select.Option key={user.collaborator_id} value={user.collaborator_id} label={user.collaborator_name}>
										<div className="flex items-center">
											<Avatar size="small" className="navbar-icon flex items-center justify-center mr-2">
												{getInitials(user.collaborator_name)}
											</Avatar>
											<div className="flex flex-col">
												<span className="text-[x-small] font-medium secondary-color truncate max-w-[200px]">{user.collaborator_name}</span>
												<span className="text-[x-small] secondary-color truncate max-w-[200px]">{user.collaborator_email}</span>
											</div>
										</div>
									</Select.Option>
								))}
							</Select>
						) : (
							<div className="flex items-center justify-between">
								<div className="flex items-center">
									<Avatar
										size="small"
										className={`flex items-center justify-center ml-2 mr-4 ${currentOwner.is_active === false ? "collab-avatar--disabled" : ""}`}
										style={{ borderColor: "var(--is-owner-color)" }}>
										{getInitials(currentOwner.collaborator_name)}
									</Avatar>
									<div className="flex flex-col">
										<span className="text-base-1 font-medium secondary-color">
											{currentOwner.collaborator_name}
										</span>
										<Tooltip title={<span className="text-[x-small] secondary-color">{currentOwner.collaborator_email}</span>}>
											<span className="text-[x-small] secondary-color">{currentOwner.collaborator_email}</span>
										</Tooltip>
									</div>
								</div>
								{isAdmin || renderedFromDTProjects || renderedFromPersonaProjects ? (
									<Tooltip
										title={
											renderedFromDTProjects
												? t("Current owner will keep Edit access after transfer")
												: renderedFromPersonaProjects
													? t("Current owner's access will be revoked from this project")
												: t("Current owner's access will be revoked from this study")
										}>
										<div onClick={() => setIsEditingOwner(true)} className="flex items-center cursor-pointer hover:opacity-80 transition-opacity mr-2">
											<i className="text-xl material-symbols-outlined primary-color mr-1">edit</i>
											<span className="primary-color text-[x-small] focus:outline-none">
												{renderedFromDTProjects || renderedFromPersonaProjects ? t("Edit project owner") : renderedFromReports ? t("Edit report owner") : t("Edit study owner")}
											</span>
										</div>
									</Tooltip>
								) : (
									<div className="flex items-center hover:opacity-80 transition-opacity mr-2">
										<span className="primary-color text-[x-small] focus:outline-none">
											{renderedFromDTProjects || renderedFromPersonaProjects ? t("Project owner") : renderedFromReports ? t("Report owner") : t("Study owner")}
										</span>
									</div>
								)}
							</div>
						)}
					</div>
				)}
				<Input
					prefix={<SearchOutlined />}
					placeholder={t("Search collaborators..")}
					className="mb-4"
					value={searchText}
					onChange={(e) => handleSearch(e.target.value)}
					allowClear
				/>
				<Table
					size="small"
					rowKey="collaborator_id"
					scroll={{ y: 260 }}
					pagination={false}
					columns={columns}
					dataSource={filteredDataSource}
					loading={loading}
					locale={{
						emptyText: loading ? t("Loading...") : t("No collaborators found"),
					}}
				/>
			</div>
		</Modal>
	);
};

export default AddCollaboratorModal;
